import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './TreasuryStrategy.css';

const TreasuryStrategy = () => {
  return (
    <>
      <Navbar />
      <main className="treasury-page" style={{ paddingTop: '80px' }}>
        {/* Two-column: Flowchart + Capital Stack side-by-side */}
        <section className="treasury-main-section">
          <div className="treasury-container-wide">
            <div className="treasury-page-header">
              <Link to="/" className="treasury-back">&larr; Home</Link>
              <h1>Fund Structuring &amp; Launch</h1>
              <p>
                Designing and launching pooled funds that align community need with investor capital
              </p>
            </div>

            {/* LEFT: Flowchart */}
            <div className="treasury-col-flow">
              <h2>Pooled Fund Structuring Model</h2>

              <div className="flow">
                {/* Step 1 */}
                <div className="flow-step">
                  <div className="flow-icon">1</div>
                  <div className="flow-card step-thesis">
                    <h3>Community Thesis Development</h3>
                    <p>
                      A <strong>community foundation</strong> identifies a pressing local need —
                      affordable housing, small business capital, climate resilience — and validates
                      the thesis through market studies, surveys, focus groups, and direct
                      engagement with community members.
                    </p>
                    <div className="flow-tags">
                      <span>Market Study</span>
                      <span>Surveys &amp; Focus Groups</span>
                      <span>Demand Validation</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 2 */}
                <div className="flow-step">
                  <div className="flow-icon">2</div>
                  <div className="flow-card step-firstloss">
                    <h3>First-Loss Commitment</h3>
                    <p>
                      The community foundation (or local funder) commits <strong>grant
                      capital</strong> as the fund's <strong>first-loss layer</strong> —
                      absorbing initial risk and signaling confidence to future investors.
                    </p>
                    <div className="flow-tags">
                      <span>Grant Capital</span>
                      <span>First-Loss Protection</span>
                      <span>Risk Signal</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 3 */}
                <div className="flow-step">
                  <div className="flow-icon">3</div>
                  <div className="flow-card step-cdfi">
                    <h3>CDFI as Fund Manager</h3>
                    <p>
                      A <strong>CDFI</strong> steps in as fund manager, contributing
                      <strong> subordinate capital</strong> alongside the first-loss layer
                      and bringing underwriting expertise, community relationships, and
                      operational infrastructure.
                    </p>
                    <div className="flow-tags">
                      <span>Subordinate Capital</span>
                      <span>Fund Management</span>
                      <span>Underwriting</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 4 */}
                <div className="flow-step">
                  <div className="flow-icon">4</div>
                  <div className="flow-card step-fundraise">
                    <h3>Institutional Fundraising</h3>
                    <p>
                      The CDFI and foundation jointly approach <strong>PRI investors</strong> and
                      <strong> senior lenders</strong> — regional banks, national banks, and
                      institutions with CRA needs — to raise senior capital, de-risked by
                      existing first-loss and subordinate layers.
                    </p>
                    <div className="flow-tags">
                      <span>PRI Investors</span>
                      <span>CRA-Motivated Banks</span>
                      <span>Senior Capital</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 5 */}
                <div className="flow-step">
                  <div className="flow-icon">5</div>
                  <div className="flow-card step-close">
                    <h3>Fund Structuring &amp; Close</h3>
                    <p>
                      The fund closes across <strong>2–3 rounds</strong>, targeting
                      <strong> ~$50M</strong>. Legal structure, governance, investment committee,
                      and reporting frameworks are established — on- or off-balance sheet.
                    </p>
                    <div className="flow-tags">
                      <span>$50M Target</span>
                      <span>On / Off Balance Sheet</span>
                      <span>Governance</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 6 — Outcomes */}
                <div className="flow-step">
                  <div className="flow-icon outcome-icon">&#10003;</div>
                  <div className="flow-card step-outcomes">
                    <h3>Outcomes &amp; Impact</h3>
                    <div className="outcome-grid">
                      <div className="outcome-item">
                        <h4>For the CDFI</h4>
                        <p>Management fees, diversified revenue, expanded lending capacity beyond its own balance sheet.</p>
                      </div>
                      <div className="outcome-item">
                        <h4>For the Foundation</h4>
                        <p>Front-row seat on community impact with transparent reporting tied to local priorities.</p>
                      </div>
                      <div className="outcome-item">
                        <h4>For the Community</h4>
                        <p>Flexible debt or equity designed from the ground up to meet real, validated needs.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Capital Stack + Clarity's Role */}
            <div className="treasury-col-side">

              {/* Capital Stack */}
              <div className="side-card">
                <h3>Illustrative Capital Stack</h3>
                <div className="cap-stack">
                  <div className="cap-layer cap-senior">
                    <div className="cap-label">Senior Capital</div>
                    <div className="cap-pct">~60%</div>
                  </div>
                  <div className="cap-layer cap-sub">
                    <div className="cap-label">Subordinate Capital</div>
                    <div className="cap-pct">~25%</div>
                  </div>
                  <div className="cap-layer cap-first">
                    <div className="cap-label">First-Loss / Grant</div>
                    <div className="cap-pct">~15%</div>
                  </div>
                </div>
                <p className="cap-note">
                  Illustrative only. Actual allocations vary by fund structure and investor requirements.
                </p>
              </div>

              {/* Where Clarity Fits In */}
              <div className="side-card clarity-role-card">
                <h3>Where Clarity Fits In</h3>
                <p className="clarity-role-intro">
                  Clarity serves as the strategic advisor across the fund lifecycle — from
                  initial concept through close and ongoing management:
                </p>
                <div className="clarity-role-list">
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Fund Structuring Model</strong>
                      <p>Building the financial model that defines capital layers, fee structures, waterfall mechanics, and investor returns.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Loan Product Design</strong>
                      <p>Developing product terms — rates, tenors, collateral requirements, flexibility provisions — that align with validated community needs.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Underwriting &amp; Approval Policies</strong>
                      <p>Drafting loan approval frameworks, credit policies, and underwriting criteria tailored to the fund's mission and risk appetite.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Pipeline Development &amp; Review</strong>
                      <p>Supporting deal sourcing, pipeline screening, and ongoing review to ensure deployment stays on-target and on-mission.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Balance Sheet &amp; Treasury Analysis</strong>
                      <p>Analyzing on- vs. off-balance sheet implications, liquidity positioning, and capital allocation to optimize the CDFI's overall financial health.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="treasury-cta">
          <div className="treasury-container">
            <h2>Let's Structure Your Fund</h2>
            <p>
              Whether you're a CDFI exploring off-balance sheet vehicles or a community foundation
              looking to catalyze local investment, we can help design the right structure.
            </p>
            <Link to="/" state={{ scrollTo: 'contact' }} className="treasury-cta-btn">
              Get in Touch &rarr;
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default TreasuryStrategy;
